import React, { ChangeEvent, useContext, useEffect, useRef, useState } from 'react';
import Modal from 'components/UI/Modal';
import styled from 'styled-components';
import FlexboxContainerComponent from 'components/UI/FlexboxContainer';
import { Check } from '@mui/icons-material';
import { Height } from 'styles/themeHeights';
import readExcelFile from 'read-excel-file';
import { SocketContext } from 'context/socket';
import UploadComponent from 'components/Product/UploadFileComponent';
import VerificationCompletionComponent from 'components/Product/VerificationCompletionComponent';
import { TEXT_LABELS, STEPS, STATUS } from '../../constants/productVariantImport';
import { checkFileSize, checkFileType, checkBrochureFileType } from 'components/Pricebooks/priceebook.utils';
import { mapDataToString } from 'utils/campaign';
import FooterComponent from './ImportFooterComponent';
import { IMPORT_STATUS } from 'constants/campaign';
import { UploadingStatus } from 'data/Service';
import { post } from 'lib/API';
import { TEXT_LABELS as PRODUCT_DETAILS_TEXT_LABELS } from 'constants/productDetails';

const SubTitle = styled.h5<any>`
  margin: 0px !important;
  font-size: 16px;
  line-height: 16px;
`;

const StepNumber = styled.span<any>`
  font-size: 16px;
  line-height: 20px;
  width: 24px;
  height: 24px;
  border-radius: 12px;
  text-align: center;
  color: #fff;
  padding: 2px 5px;
  margin-bottom: 4px;
  background: ${({ $bg }) => ($bg ? '#F7991C' : '#bfc2c5')};
`;

interface Props {
    show: boolean;
    onClose: () => void;
    isUploading: boolean;
    setIsUploading: (value: boolean) => void;
    loadData?: () => void;
    progressSocket?: string;
    importResultSocket?: string;
    uploadData: () => void;
    heading?: string;
    setExcelData: (excelData: any) => void;
    allowAnyFormat?: boolean;
    isGenAI?: boolean;
    onViewResult?: () => void;
    isSuccess?: boolean;
    downloadTemplateFileName?: string;
    fileHeaderValidator?: (file: File | null) => Promise<boolean>;
    step: number;
    setStep: (step: number) => void;
    uploadingStatus: UploadingStatus;
    setUploadingStatus: (uploadingStatus: UploadingStatus | null) => void;
    label?: string;
    container?: string;
    setFileName?: (fileName: string) => void;
    vendorId?: number;
    token?: string;
}

const ImportModal: React.FC<Props> = ({
    show,
    onClose,
    isUploading,
    setIsUploading,
    loadData,
    progressSocket,
    importResultSocket,
    uploadData,
    heading = 'Upload',
    setExcelData,
    downloadTemplateFileName,
    fileHeaderValidator,
    step,
    setStep,
    uploadingStatus,
    setUploadingStatus,
    allowAnyFormat = false,
    isGenAI = false,
    onViewResult,
    isSuccess = false,
    container = undefined,
    label = '',
    vendorId,
    token
}) => {
    const [uploadSuccess, setUploadSuccess] = useState(false);
    const [fileUploading, setFileUploading] = useState(false);
    const [cancelModal, setCancelModal] = useState(false);
    const [uploadFileSizeError, setUploadFileSizeError] = useState(false);
    const [uploadFileTypeError, setUploadFileTypeError] = useState(false);
    const [uploadFileTemplateError, setUploadFileTemplateError] = useState(false);
    const [progressPercent, setProgressPercent] = useState<number>(0);
    const [progressMessage, setProgressMessage] = useState<string>('');
    const [excelFile, setExcelFile] = useState<string>();
    const [isUploadCancelled, setIsUploadCancelled] = useState(false);
    const [file, setFile] = useState<any>({
        imageFile: [],
        uploadDate: new Date(),
    });
    const inputRef = useRef<HTMLInputElement | null>(null);
    const socket = useContext(SocketContext);
    const isUploadCancelledRef = useRef<boolean>(isUploadCancelled);


    const handleClear = () => {
        if (inputRef.current != null) {
            inputRef.current.value = '';
        }
        setUploadFileSizeError(false);
        setUploadFileTypeError(false);
        setUploadFileTemplateError(false);
        setUploadSuccess(false);
        setFileUploading(false);
        setIsUploadCancelled(false); // Reset cancellation flag
        setFile({
            imageFile: [],
            uploadDate: '',
        });
        setStep(STEPS.UPLOADING)
        setUploadingStatus(null)
    };

    const handleCancelUpload = async () => {
        // FIRST: Close the modal immediately
        onClose();

        // Set cancellation flag to prevent further processing
        setIsUploadCancelled(true);

        // Stop the upload process
        setFileUploading(false);
        setIsUploading(false);
        setUploadSuccess(false);

        // Reset file state
        setFile({
            imageFile: [],
            uploadDate: '',
        });

        // Reset to upload step
        setStep(STEPS.UPLOADING);

        // Clear input
        if (inputRef.current != null) {
            inputRef.current.value = '';
        }

        // Disconnect socket listeners to stop receiving updates
        if (progressSocket && importResultSocket) {
            socket.off(progressSocket);
            socket.off(importResultSocket);
        }

        // THEN: Cancel the backend process in the background
        if (isGenAI && vendorId && token) {
            try {
             await post('v2/products/brochure/cancel', token, { vendorId });
            } catch (error) {
                console.error('Error cancelling backend process:', error);
            }
        }
    };

    const handleUploadClick = () => {
        if (inputRef.current != null) {
            inputRef.current.value = '';
        }
        setUploadFileSizeError(false);
        setUploadFileTypeError(false);
        setUploadFileTemplateError(false);
        setUploadSuccess(false);
        setFileUploading(false);
        setIsUploadCancelled(false); // Reset cancellation flag for new upload
        setFile({
            imageFile: [],
            uploadDate: '',
        });
        inputRef.current?.click();
    };

    const handleFileChange = async (e: ChangeEvent<HTMLInputElement>) => {
        if (!e.target.files) {
            return;
        }
        setFile({
            imageFile: [e.target.files[0]],
            uploadDate: Date.now(),
        });

        if (!checkFileSize(e.target.files[0])) {
            setUploadFileSizeError(true);
            setFileUploading(false);
            return;
        }

        if (isGenAI) {
            // For product brochure uploads, validate specific file types
            if (checkBrochureFileType(e.target.files[0])) {
                setUploadFileTypeError(false);
            } else {
                setUploadFileTypeError(true);
                return;
            }
        } else if (!allowAnyFormat) {
            // For other Excel imports, validate .xlsx only
            if (checkFileType(e.target.files[0])) {
                setUploadFileTypeError(false);
            } else {
                setUploadFileTypeError(true);
                return;
            }
        }
    };

    const templateVerify = async () => {
        // Don't proceed if upload was cancelled
        if (isUploadCancelled) {
            return;
        }

        const importFile = file.imageFile[0];
        if (allowAnyFormat) {
            setExcelData(importFile);
            setExcelFile(importFile.name);
            setFileUploading(false);
            setUploadSuccess(true);
        } else {
            const data = await readExcelFile(file.imageFile[0]);
            setExcelData(mapDataToString(data));
            setExcelFile(importFile.name);

            const templateVerification = fileHeaderValidator ? await fileHeaderValidator(importFile) : true;
            if (templateVerification) {
                setFileUploading(false);
                setUploadFileTemplateError(false);
                setUploadSuccess(true);
            } else {
                setUploadFileTemplateError(true);
                setUploadSuccess(false);
                return;
            }
        }
    };

    useEffect(() => {
        if (
            file.imageFile &&
            file.imageFile.length > 0 &&
            !uploadFileSizeError &&
            !uploadFileTypeError
        ) {
            templateVerify();
        }
    }, [file.imageFile, uploadFileSizeError, uploadFileTypeError]);

    const gotoNextStep = () => {
        // Don't proceed if upload was cancelled
        if (isUploadCancelled) {
            return;
        }

        setFileUploading(true);
        setUploadSuccess(false);

        setTimeout(() => {
          // Check again if upload was cancelled during the delay
          if (isUploadCancelledRef.current) {
            return;
          }
          uploadData();
          setFileUploading(false);
          setStep(STEPS.VERIFICATION);
        }, 2000);
    };

    useEffect(() => {
        if (!progressSocket || !importResultSocket) return;

        const handleProgress = (result: any) => {
            // Don't process if upload was cancelled
            if (isUploadCancelled) {
                return;
            }

            if (step !== STEPS.VERIFICATION) {
                setIsUploading(false);
                setFileUploading(false);
                setStep(STEPS.VERIFICATION);
                setUploadingStatus(result.uploadingStatus);
            }
            // Only update progressPercent if it's provided and valid
            if (result.progressPercent !== undefined && result.progressPercent !== null) {
                setProgressPercent(result.progressPercent);
            }
            setProgressMessage(result.message || '');
        };

        const handleUpload = (result: any) => {
            // Don't process if upload was cancelled
            if (isUploadCancelled) {
                return;
            }

            if (result?.status === IMPORT_STATUS.COMPLETED) {
                setStep(STEPS.COMPLETION);
                setFileUploading(false);
                setIsUploading(false);
                setUploadingStatus(result?.importData);
                if (result?.importData?.totalCount > 0 && result?.importData?.passedCount > 0) loadData?.();
            }
            setProgressPercent(0);
        };

        const handleCancelled = () => {
            // Handle backend cancellation confirmation
            setIsUploadCancelled(true);
            setFileUploading(false);
            setIsUploading(false);
            setStep(STEPS.UPLOADING);
            setProgressPercent(0);
        };

        socket.on(progressSocket, handleProgress);
        socket.on(importResultSocket, handleUpload);
        
        // Listen for cancellation events from backend
        if (isGenAI && vendorId) {
            socket.on(`product_import_cancelled_${vendorId}`, handleCancelled);
        }

        isUploadCancelledRef.current = isUploadCancelled;

        return () => {
            socket.off(progressSocket, handleProgress);
            socket.off(importResultSocket, handleUpload);
            if (isGenAI && vendorId) {
                socket.off(`product_import_cancelled_${vendorId}`, handleCancelled);
            }
            setFile({
                imageFile: [],
                uploadDate: new Date()
            });
        };
    }, [progressSocket, importResultSocket, step, isUploadCancelled, isGenAI, vendorId]);

    const isStepTwoFileVerification =
        step !== STEPS.UPLOADING &&
        (!uploadingStatus || uploadingStatus?.statusId === STATUS.PENDING);
    const showNextButton =
        isStepTwoFileVerification || (uploadSuccess && step === STATUS.PENDING);
    const disableNextButton = isStepTwoFileVerification || fileUploading;

    return (
        <Modal
            width="825px"
            showModal={cancelModal ? false : show}
            onClose={() => {
                if (!isUploading) onClose();
            }}
            showButtons={false}
            title={heading}
            modalFooterMargin="0"
            left="50vw"
            marginLeft="-412px"
            iconName={isGenAI ? 'ProductBrochureBlack' : 'PricebookModalIcon'}
            showCloseIcon={!isUploading}
        >
            <FlexboxContainerComponent
                bgColor="#F9F9FA"
                border="1px solid #E1E6EB"
                borderRadius="6px"
                padding="24px"
                flexDirection="column"
            >
                <FlexboxContainerComponent margin="0 auto" flexDirection="row">
                    <FlexboxContainerComponent
                        flexDirection="column"
                        alignItems="center"
                        width="24px"
                    >
                        <StepNumber $bg={step >= STEPS.UPLOADING}>
                            {step === STEPS.UPLOADING ? (
                                <Check
                                    style={{
                                        color: '#FFFFFF',
                                        height: Height.xsPlus,
                                        width: '12px',
                                    }}
                                />
                            ) : (
                                <>{STEPS.UPLOADING}</>
                            )}
                        </StepNumber>
                        <SubTitle>{TEXT_LABELS.UPLOAD}</SubTitle>
                    </FlexboxContainerComponent>
                    <hr
                        style={{
                            width: '150px',
                            color: '#C1C9D1',
                            margin: '12px 4px',
                            height: '1px',
                        }}
                    ></hr>
                    <FlexboxContainerComponent
                        flexDirection="column"
                        alignItems="center"
                        width="24px"
                    >
                        <StepNumber $bg={step >= STEPS.VERIFICATION}>
                            {step === STEPS.VERIFICATION ? (
                                <Check
                                    style={{
                                        color: '#FFFFFF',
                                        height: Height.xsPlus,
                                        width: '12px',
                                    }}
                                />
                            ) : (
                                <>{STEPS.VERIFICATION}</>
                            )}
                        </StepNumber>
                        <SubTitle>{isGenAI ? TEXT_LABELS.PROCESSING : TEXT_LABELS.VERIFICATION}</SubTitle>
                    </FlexboxContainerComponent>
                    <hr
                        style={{
                            width: '150px',
                            color: '#C1C9D1',
                            margin: '12px 4px',
                            height: '1px',
                        }}
                    ></hr>
                    <FlexboxContainerComponent
                        flexDirection="column"
                        alignItems="center"
                        width="24px"
                    >
                        <StepNumber $bg={step >= STEPS.COMPLETION}>
                            {step === STEPS.COMPLETION ? (
                                <Check
                                    style={{
                                        color: '#FFFFFF',
                                        height: Height.xsPlus,
                                        width: '12px',
                                    }}
                                />
                            ) : (
                                <>{STEPS.COMPLETION}</>
                            )}
                        </StepNumber>
                        <SubTitle>{TEXT_LABELS.COMPLETED}</SubTitle>
                    </FlexboxContainerComponent>
                </FlexboxContainerComponent>
                {step === STEPS.UPLOADING && (
                    <UploadComponent
                        uploadSuccess={uploadSuccess}
                        uploadFileSizeError={uploadFileSizeError}
                        uploadFileTypeError={uploadFileTypeError}
                        uploadFileTemplateError={uploadFileTemplateError}
                        file={file}
                        handleUploadClick={handleUploadClick}
                        handleClear={handleClear}
                        inputRef={inputRef}
                        handleFileChange={handleFileChange}
                        fileUploading={fileUploading}
                        buttonText={isGenAI ? TEXT_LABELS.UPLOAD_PRODUCT_BROCHURE : TEXT_LABELS.UPLOAD_PRODUCT_VARIANTS}
                        fileTypeErrorMessage={isGenAI ? TEXT_LABELS.FILE_FORMAT_BROCHURE : TEXT_LABELS.FILE_FORMAT_XLXS}
                        acceptedFileTypes={isGenAI ? '.pdf,.docx,.doc,.xlsx,.xls' : '.xlsx'}
                        onCancelUpload={handleCancelUpload}
                    />
                )}
                {(step === STEPS.VERIFICATION || step === STEPS.COMPLETION) && (
                    <VerificationCompletionComponent
                        uploadingStatus={uploadingStatus}
                        progressPercent={progressPercent}
                        progressMessage={progressMessage}
                        step={step}
                        importedLabel={TEXT_LABELS.ENTRIES_IMPORTED.replace('{{label}}', label)}
                        failedLabel={TEXT_LABELS.ENTRIES_FAILED.replace('{{label}}', label)}
                        isGenAI={isGenAI}
                    />
                )}
                {isGenAI && (
                    <div style={{ alignSelf: 'center', width: 500, marginTop: 20, textAlign: 'center', fontWeight: 'bold' }}>
                        {
                            step === STEPS.UPLOADING ?
                                'Upload your product brochure (PDF or document) and let AI generate the product details.' :
                                step === STEPS.VERIFICATION ?
                                    'Analyzing brochure using Gen AI... Please wait while AI extracts the product details.' :
                                    step === STEPS.COMPLETION && isSuccess ?
                                        'The product details have been successfully generated. Please review them carefully before publishing.' :
                                        step === STEPS.COMPLETION && !isSuccess ?
                                            'Your upload has encountered an error while extracting product details. Please try again.' : ''
                        }
                    </div>
                )}
                <FooterComponent
                    showNextButton={showNextButton} disableNextButton={disableNextButton}
                    gotoNextStep={gotoNextStep}
                    uploadSuccess={uploadSuccess}
                    uploadingStatus={uploadingStatus}
                    step={step}
                    handleClear={isGenAI && isSuccess ? onViewResult! : handleClear}
                    downloadTemplateFileName={downloadTemplateFileName!}
                    isGenAI={isGenAI}
                    isSuccess={isSuccess}
                    container={container}
                />

                {isGenAI && (
                <div style={{ alignSelf: 'center', marginTop: 20, textAlign: 'center', fontWeight: 'lighter', fontSize: '12px', color:'#70777E' }}>
                {PRODUCT_DETAILS_TEXT_LABELS.AI_CAN_MAKE_MISTAKES}
                </div>
                )}
            </FlexboxContainerComponent>
        </Modal>
    );
};

export default React.memo(ImportModal);
